<template>
  <div class="rotatable-camera" :style="wrapStyle">
    <img
      v-if="url"
      :src="url"
      ref="img"
      :style="imgStyle"
      @load="onLoad"
    />
    <v-btn
      v-if="controls && url"
      icon
      x-small
      dark
      class="rc-rotate-btn"
      title="Rotate 90° CW"
      @click.stop.prevent="rotate"
    >
      <v-icon small>mdi-rotate-right</v-icon>
    </v-btn>
  </div>
</template>

<script>
// Reusable camera feed rendered at a given rotation (degrees clockwise).
//
// The rotation value is OWNED BY THE PARENT — for student views it comes from the
// admin-set value stored in the camera config (rl_experiment `value` JSON); this
// component just applies it. When `controls` is true (the admin preview) it also
// shows a rotate button and emits the next angle so the parent form can save it.
//
// Handles the bounding box: a plain CSS rotate() keeps the original layout box and
// overflows at 90°/270°, so we swap the aspect box + image sizing to keep the
// rotated feed filling the card at any quarter-turn.
export default {
  name: "RotatableCamera",
  props: {
    url: { type: String, default: "" },
    rotation: { type: Number, default: 0 },
    controls: { type: Boolean, default: false },
  },
  data() {
    return {
      natAspect: 4 / 3, // width / height; refined once the real frame loads
    };
  },
  computed: {
    isQuarter() {
      return (((this.rotation % 180) + 180) % 180) !== 0;
    },
    wrapStyle() {
      const a = this.natAspect || 4 / 3;
      const paddingBottom = this.isQuarter ? a * 100 : 100 / a;
      return {
        position: "relative",
        width: "100%",
        overflow: "hidden",
        paddingBottom: paddingBottom + "%",
        background: "#000",
      };
    },
    imgStyle() {
      const a = this.natAspect || 4 / 3;
      const w = this.isQuarter ? a * 100 : 100;
      const h = this.isQuarter ? (1 / a) * 100 : 100;
      return {
        position: "absolute",
        top: "50%",
        left: "50%",
        width: w + "%",
        height: h + "%",
        objectFit: "contain",
        transform: "translate(-50%, -50%) rotate(" + this.rotation + "deg)",
        transformOrigin: "center center",
      };
    },
  },
  methods: {
    onLoad(e) {
      const im = e && e.target;
      if (im && im.naturalWidth && im.naturalHeight) {
        this.natAspect = im.naturalWidth / im.naturalHeight;
      }
    },
    rotate() {
      this.$emit("rotate", (((this.rotation + 90) % 360) + 360) % 360);
    },
  },
  beforeDestroy() {
    // Stop the MJPEG stream so it doesn't keep running after the view closes.
    if (this.$refs.img) {
      this.$refs.img.src = "";
    }
  },
};
</script>

<style scoped>
.rotatable-camera {
  line-height: 0;
}
.rc-rotate-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 3;
  background: rgba(0, 0, 0, 0.45) !important;
}
</style>
